import * as React from "react";
import styled from "styled-components";
import {
  Body,
  DataGrid,
  DataGridHeader,
  DataGridHeading,
  DataGridRow,
  DataGridCell,
  Main,
  color,
} from "@conserveit/blocks";
import { IndexByNumber } from "../../../redux/modules/insights/utils";

interface Group {
  group_id: number;
  group_key: string;
  group_name: string;
  group_type: keyof GroupTypeList;
  project?: string;
  /** timestamp */
  createdAt: string;
  /** timestamp */
  updatedAt: string;
}

interface GroupTypeList {
  group: string;
  project: string;
  site: string;
}

type Projects = IndexByNumber<Insite.Project>;
interface ProjectList {
  /** This is keyed by the Project id or key */
  [key: string]: string;
}
interface OwnProps {
  projects: Projects;
  groups: Group[];
  groupTypeList: GroupTypeList;
  projectList: ProjectList;
  onRowSelect: (group: Group) => void;
}
const StyledDataGridRow = styled(DataGridRow)`
  cursor: pointer;
`;

const StyledBlueBody = styled(Body)`
  color: ${color.theme.primaryColor.action.primary};
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const formatProjectList = (projects: Projects) =>
  Object.values(projects).reduce((keyLabel: any, project) => {
    keyLabel[project.jira_key] = project.project_name;
    return keyLabel;
  }, {});

export const GroupGrid = React.memo<OwnProps>(
  (props) => {
    const [projectList, setProjectList] = React.useState<ProjectList>(
      formatProjectList(props.projects)
    );
    const { projects, groups, groupTypeList, onRowSelect } = props;

    const formatGroupType = React.useCallback(
      (groupType: keyof GroupTypeList) => groupTypeList[groupType],
      [groupTypeList]
    );
    const formatProject = React.useCallback(
      (project?: string) =>
        project ? `${project}-${projectList[project]}` : "",
      [projectList]
    );

    React.useEffect(() => {
      setProjectList(formatProjectList(projects));
    }, [projects]);

    return (
      <DataGrid grow={[1]} cellProps={{ type: "dense", density: "default" }}>
        <DataGridHeader>
          <DataGridHeading>
            <Main>Group Name</Main>
          </DataGridHeading>
          <DataGridHeading>
            <Main>Group Type</Main>
          </DataGridHeading>
          <DataGridHeading>
            <Main>Project</Main>
          </DataGridHeading>
          <DataGridHeading>
            <Main>Action</Main>
          </DataGridHeading>
        </DataGridHeader>
        {groups.map((group) => (
          <StyledDataGridRow
            data-cy="group-row"
            onClick={() => onRowSelect(group)}
            key={group.group_id}
          >
            <DataGridCell>
              <Main>{group.group_name}</Main>
            </DataGridCell>
            <DataGridCell minWidth="250px">
              <Main>{formatGroupType(group.group_type)}</Main>
            </DataGridCell>
            <DataGridCell minWidth="250px">
              <Main>{formatProject(group.project)}</Main>
            </DataGridCell>
            <DataGridCell minWidth="150px">
              <StyledBlueBody>Edit</StyledBlueBody>
            </DataGridCell>
          </StyledDataGridRow>
        ))}
      </DataGrid>
    );
  }
);
